import DigiobcanApi from '../../../../api/DigiobcanApi';
import CopyIdChip from '../../../../components/CopyIdChip';
import MissingInternetConnectionIndicator from '../../../../components/MissingInternetConnectionIndicator';
import NotFound from '../../../../components/NotFound';
import { adminGetVotingDetail } from '../../../../features/queries/adminGetVotingDetail';
import { entityId, shortId } from '../../../../lib/ids';
import { QueryNameSpace, useM, useQ } from '../../../../lib/query';
import {
  NEW_VOTING_PARAM,
  votingState,
  type QuestionType,
} from '../../../../lib/votings';
import { useLayout } from '../../../../providers/LayoutProvider';
import { useLogger } from '../../../../providers/LoggerProvider';
import { useResponsive } from '../../../../providers/ResponsiveProvider';
import { __, toastError, toastSuccess } from '../../../../utils';
import PublishDialog from './PublishDialog';
import QuestionEditor from './QuestionEditor';
import VotingBuilderSidebar, {
  type BuilderSelection,
} from './VotingBuilderSidebar';
import VotingEmptyState from './VotingEmptyState';
import VotingLockedView from './VotingLockedView';
import VotingSettingsForm from './VotingSettingsForm';
import {
  emptyQuestion,
  toApiValues,
  toFormValues,
  type ApiFormValues,
  type FormValues,
  type QuestionValues,
} from './formValues';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import arrayMutators from 'final-form-arrays';
import React from 'react';
import { Form, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useNavigate, useParams } from 'react-router';

const votingErrorMessages: Record<string, string> = {
  VOTING_ALREADY_ACTIVE: __(
    'Hlasovanie sa nedá upraviť, pretože už je aktívne.',
  ),
  VOTING_INVALID_DATES: __(
    'Neplatné dátumy. Skontrolujte dátum otvorenia a zatvorenia.',
  ),
  VOTING_INVALID_END_DATE: __(
    'Neplatný dátum zatvorenia. Musí byť po dátume otvorenia.',
  ),
  VOTING_INVALID_OPTIONS_RANGE: __(
    'Neplatný rozsah možností. Skontrolujte minimálny a maximálny počet odpovedí.',
  ),
  VOTING_INSUFFICIENT_OPTIONS: __(
    'Nedostatočný počet možností pre požadovaný rozsah odpovedí.',
  ),
  INVALID_OPTION_URL: __(
    'Neplatný odkaz v jednej z možností. Skontrolujte všetky URL.',
  ),
  VOTING_UPDATE_FAILED: __('Úprava hlasovania zlyhala. Skúste to znovu.'),
  VOTING_NOT_FOUND: __('Hlasovanie nebolo nájdené.'),
};

/** Otázky z formulárového tvaru do tela požiadavky. */
const toApiQuestions = (questions: ApiFormValues['questions']) =>
  questions.map((q) => ({
    title: q.title,
    description: q.description,
    type: q.type,
    min_options: q.minOptions,
    max_options: q.maxOptions,
    scale_min: q.scaleMin,
    scale_max: q.scaleMax,
    scale_min_label: q.scaleMinLabel,
    scale_max_label: q.scaleMaxLabel,
    options: q.options,
  }));

/**
 * Tvorba, úprava a životný cyklus hlasovania.
 *
 * Obrazovka má tri podoby a rozhoduje o nich stav hlasovania, nie prepínač:
 *
 * - **koncept** je builder - vľavo zoznam (nastavenia + otázky), vpravo editor
 *   jednej otázky ([[VotingBuilderSidebar]], [[QuestionEditor]]);
 * - **otvorené, uzavreté alebo archivované** je zápis ([[VotingLockedView]]),
 *   nie vypnutý formulár;
 * - **Výsledky** sú tab, ktorý pribudne, keď je čo ukázať.
 *
 * Publikovanie ide cez kontrolu so súhrnom ([[PublishDialog]]). Nahradila tri
 * varianty potvrdenia o chýbajúcom dátume, ktoré sa pýtali na jednu vec
 * a o zvyšku hlasovania mlčali.
 *
 * Priebežné ukladanie **nie je** a nesľubuje sa: hlasovanie sa vytvára jedným
 * `adminCreateVoting` na konci, takže pätička hlási „Neuložené zmeny" a ukladá
 * sa tlačidlom. Keby API dostalo koncepty, je to jediné miesto, kde to treba
 * zmeniť.
 */
const VotingDetailAdminPage: React.FC = () => {
  const { logger } = useLogger();
  const queryClient = useQueryClient();
  const { votingId: votingIdParam } = useParams() as { votingId: string };
  /**
   * Sentinel „nové hlasovanie" sa porovnáva na surovom parametri, nie po
   * `entityId` - detail v [[votings]].
   */
  const isNewVoting = votingIdParam === NEW_VOTING_PARAM;
  const votingId = isNewVoting ? '' : entityId(votingIdParam, 'voting');

  const navigate = useNavigate();
  const { isMd } = useResponsive();
  const { setPageTitle, setTabs, setFooter, setActions } = useLayout();

  const [selection, setSelection] =
    React.useState<BuilderSelection>('settings');
  /** Na mobile je zoznam a editor dve obrazovky (návrh 7a a 7b). */
  const [mobilePane, setMobilePane] = React.useState<'list' | 'editor'>('list');
  const [isFormDirty, setIsFormDirty] = React.useState(false);
  const [publishValues, setPublishValues] = React.useState<FormValues | null>(
    null,
  );
  /** Čo sa má stať po `handleSubmit` - uloženie alebo kontrola pred publikovaním. */
  const intentRef = React.useRef<'save' | 'publish'>('save');

  /**
   * **Hodiny sa čítajú pri každom renderi, nie raz pri mountnutí.**
   *
   * Stav hlasovania nie je v dátach, počíta sa z dátumov oproti „teraz"
   * ([[votings]]) - a publikovanie aj uzavretie zapisuje serverový čas *teraz*.
   * Oproti hodinám zamrznutým pri otvorení obrazovky je taký dátum ešte
   * v budúcnosti, takže obrazovka po publikovaní ostala v koncepte a po
   * uzavretí hlásila „prebieha", kým to úradník neobnovil klávesom F5.
   * Rovnako to platí pre [[VotingLockedView]] a [[VotingAdminSummary]].
   */
  const now = new Date();

  const {
    data: votingDetail,
    isLoading,
    isError,
    refetch,
  } = useQ(adminGetVotingDetail, { votingId }, { enabled: !isNewVoting });

  /**
   * **Zmena stavu hlasovania nemení len tento detail.** Publikovanie,
   * uzavretie aj archivácia sa dovtedy dočkali len `refetch`-u detailu, takže
   * správa hlasovaní aj občiansky zoznam s detailom a výsledkami ukazovali
   * ešte starý stav - `staleTime` je minúta, takže úradník musel appku
   * obnoviť klávesom F5, aby uvidel, čo práve urobil.
   *
   * Detail sa preto načíta znova a zvyšku dotazov nad hlasovaniami sa zhodí
   * cache; čaká sa na všetko, aby obrazovka neprepla skôr, než sú dáta.
   */
  const refreshVotings = React.useCallback(
    () =>
      Promise.all([
        refetch(),
        ...[
          QueryNameSpace.adminGetVotings,
          QueryNameSpace.getVotings,
          QueryNameSpace.getVotingDetail,
          QueryNameSpace.getVotingResults,
        ].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey: [queryKey] }),
        ),
      ]),
    [queryClient, refetch],
  );

  /**
   * **`initialValues` musí byť stabilná referencia.**
   *
   * `react-final-form` porovnáva `initialValues` plytko a pri zmene formulár
   * reinicializuje. `toFormValues(...)` volané priamo v JSX vyrába nový objekt
   * pri každom renderi a `questions` je v ňom nové pole, takže sa formulár
   * reinicializoval **na každý render** - aj na taký, ktorý spustilo len
   * preklikanie zo `Nastavení` na otázku.
   *
   * Samo o sebe by to prežilo (`keepDirtyOnReinitialize` rozpísané hodnoty
   * drží), lenže ten istý render zároveň **odmountuje** `VotingSettingsForm`.
   * Odregistrované pole už nie je „dirty", takže reinicializácia ho vrátila na
   * počiatočnú hodnotu - napísaný názov a popis zmizli presne pri prepnutí na
   * otázku.
   *
   * Takto sa formulár reinicializuje len vtedy, keď sa naozaj zmenia dáta zo
   * servera (po uložení a refetchi), a `keepDirtyOnReinitialize` tam drží to,
   * čo medzitým niekto dopísal.
   */
  const initialValues = React.useMemo(
    () => toFormValues(votingDetail),
    [votingDetail],
  );

  const state = votingDetail ? votingState(votingDetail, now) : undefined;
  const isEditingLocked = !!votingDetail && state !== 'draft';

  const createMutation = useM(
    (value: ApiFormValues) =>
      DigiobcanApi.adminCreateVoting(undefined, {
        title: value.title,
        description: value.description,
        open_from: value.openFrom?.toISOString() || undefined,
        open_to: value.openTo?.toISOString() || undefined,
        verified_accounts_only: value.verifiedAccountsOnly,
        allow_anonymous: value.allowAnonymous,
        public: value.public,
        available_results: value.availableResults,
        available_incomplete_results: value.availableIncompleteResults,
        report_enabled: value.sendReport,
        report_emails: value.reportEmails,
        questions: toApiQuestions(value.questions),
      }),
    {
      onError: (e, variables) => {
        logger.error('Error creating voting', {
          action: 'createVoting',
          error: e,
          data: {
            ...variables,
            open_to: variables.openTo?.toISOString() || undefined,
            open_from: variables.openFrom?.toISOString() || undefined,
            questions: toApiQuestions(variables.questions),
          },
        });
        toastError(
          votingErrorMessages[e.details?.name] ??
            __('Hlasovanie sa nepodarilo vytvoriť.'),
        );
      },
    },
  );

  const updateMutation = useM(
    ({
      openFrom,
      openTo,
      questions,
      verifiedAccountsOnly,
      allowAnonymous,
      public: isPublic,
      availableResults,
      availableIncompleteResults,
      // `sendReport` a `reportEmails` sa musia rozbaliť, aj keď sa dosadzujú
      // nižšie: `...rest` by ich poslal pod appkovými menami, ktoré kontrakt
      // nepozná - a keďže telo nemá `additionalProperties: false`, server by
      // ich ticho zahodil a nastavenie reportu by sa neuložilo.
      sendReport,
      reportEmails,
      ...rest
    }: ApiFormValues) =>
      DigiobcanApi.adminUpdateVoting(votingId, undefined, {
        ...rest,
        open_from: openFrom ? openFrom.toISOString() : null,
        open_to: openTo ? openTo.toISOString() : null,
        verified_accounts_only: verifiedAccountsOnly,
        allow_anonymous: allowAnonymous,
        public: isPublic,
        available_results: availableResults,
        available_incomplete_results: availableIncompleteResults,
        report_enabled: sendReport,
        report_emails: reportEmails,
        questions: toApiQuestions(questions),
      }),
    {
      onError: (e, variables) => {
        logger.error('Error editing voting', {
          action: 'editVoting',
          error: e,
          data: {
            ...variables,
            open_from: variables.openFrom?.toISOString() ?? null,
            open_to: variables.openTo?.toISOString() ?? null,
            questions: toApiQuestions(variables.questions),
          },
        });
        toastError(
          votingErrorMessages[e.details?.name] ??
            __('Hlasovanie sa nepodarilo upraviť.'),
        );
      },
    },
  );

  const isSaving = createMutation.isPending || updateMutation.isPending;

  /** Neuložený builder nesmie zmiznúť s kartou prehliadača. */
  React.useEffect(() => {
    if (!isFormDirty) return;
    const handler = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isFormDirty]);

  React.useEffect(() => {
    setActions([
      { children: <MissingInternetConnectionIndicator /> },
      // Identifikátor vpravo v hlavičke a **kopírovateľný** - v titule stránky
      // je odteraz meno hlasovania, nie ID. Holý koncový segment, ako všade.
      ...(votingDetail
        ? [
            {
              children: (
                <CopyIdChip
                  code={shortId(votingDetail.voting_id)}
                  label={__('Kopírovať ID hlasovania')}
                />
              ),
            },
          ]
        : []),
    ]);
  }, [setActions, votingDetail]);

  React.useEffect(() => {
    setPageTitle(
      isNewVoting
        ? __('Nové hlasovanie')
        : votingDetail?.title || __('Hlasovanie'),
    );
  }, [isNewVoting, setPageTitle, votingDetail]);

  /**
   * **Detail hlasovania taby nemá.** Boli dva („Hlasovanie" a „Výsledky") a tá
   * istá vec bola napísaná v oboch - počet hlasov, publikum aj zoznam otázok -
   * takže sa úradník prepínaním dozvedal to isté dvakrát a číslo bolo v každom
   * tabe zaokrúhlené inak. Zápis, výsledky a akcie sú odteraz jedna stránka
   * ([[VotingLockedView]]); koncept je builder. `PageHeader` pri prázdnom poli
   * lištu tabov nekreslí vôbec.
   */
  React.useEffect(() => {
    setTabs([]);
  }, [setTabs]);

  /** Pätička patrí builderu; zápis (`isEditingLocked`) akcie nesie v páse. */
  React.useEffect(() => {
    if (isEditingLocked || (!isNewVoting && !votingDetail)) {
      setFooter(null);
      return;
    }

    if (!isMd && mobilePane === 'editor') {
      setFooter(
        <Button fullWidth onClick={() => setMobilePane('list')}>
          {__('Hotovo')}
        </Button>,
      );
      return () => setFooter(null);
    }

    setFooter(
      <Stack direction='row' alignItems='center' gap={1.5}>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ display: { xs: 'none', sm: 'block' } }}
        >
          {isFormDirty ? __('Neuložené zmeny') : __('Všetko uložené')}
        </Typography>
        <Stack direction='row' gap={1} sx={{ ml: 'auto' }}>
          <Button
            variant='outline'
            size={isMd ? 'medium' : 'small'}
            form='voting-builder-form'
            type='submit'
            loading={isSaving}
            onClick={() => {
              intentRef.current = 'save';
            }}
          >
            {isNewVoting ? __('Uložiť koncept') : __('Uložiť')}
          </Button>
          <Button
            size={isMd ? 'medium' : 'small'}
            form='voting-builder-form'
            type='submit'
            disabled={isSaving}
            onClick={() => {
              intentRef.current = 'publish';
            }}
          >
            {__('Publikovať')}
          </Button>
        </Stack>
      </Stack>,
    );

    return () => setFooter(null);
  }, [
    isEditingLocked,
    isFormDirty,
    isMd,
    isNewVoting,
    isSaving,
    mobilePane,
    setFooter,
    votingDetail,
  ]);

  /**
   * Uloženie. `publish` navyše otvorí hlasovanie hneď, keď nemá dátum
   * otvorenia - inak by „Publikovať" skončilo konceptom, ktorý nikto nevidí.
   */
  const save = React.useCallback(
    async (values: FormValues, publish: boolean) => {
      if (!navigator.onLine) {
        toastError(__('Internetové pripojenie zlyhalo.'));
        return;
      }

      const titles = values.questions.map(({ title }) => title);
      if (titles.some((title, index) => titles.indexOf(title) !== index)) {
        toastError(__('Otázky nemôžu mať rovnaký názov.'));
        return;
      }

      const apiValues = toApiValues(values);

      try {
        const saved = isNewVoting
          ? await createMutation.mutateAsync(apiValues)
          : await updateMutation.mutateAsync(apiValues);

        if (publish && !apiValues.openFrom) {
          await DigiobcanApi.adminUpdateVotingStatus(
            saved.voting_id,
            undefined,
            { status: 'open' },
          );
        }

        toastSuccess(
          publish
            ? __('Hlasovanie bolo publikované.')
            : isNewVoting
              ? __('Hlasovanie bolo vytvorené.')
              : __('Hlasovanie bolo uložené.'),
        );

        if (isNewVoting) {
          // Detail sa načíta sám, keď adresa dostane ID; v zozname správy
          // hlasovaní by sa nové hlasovanie inak objavilo až o minútu.
          queryClient.invalidateQueries({
            queryKey: [QueryNameSpace.adminGetVotings],
          });
          navigate(`/administracia/hlasovania/${shortId(saved.voting_id)}`);
        } else {
          await refreshVotings();
        }
      } catch {
        // Chybu hlási mutácia; `adminUpdateVotingStatus` má vlastnú hlášku.
      }
    },
    [
      createMutation,
      isNewVoting,
      navigate,
      queryClient,
      refreshVotings,
      updateMutation,
    ],
  );

  if (isError) {
    return (
      <NotFound
        message={__('Hlasovanie nebolo nájdené alebo neexistuje.')}
        backUrl='/administracia/hlasovania'
        backText={__('Späť na zoznam hlasovaní')}
      />
    );
  }

  if (isLoading && !isNewVoting) {
    return (
      <Box sx={{ p: 1.5 }}>
        <Skeleton variant='rounded' height={420} />
      </Box>
    );
  }

  if (isEditingLocked && votingDetail) {
    // Bez odsadeného obalu: zápis je flush pás vľavo + plocha vpravo a musí
    // siahať k okrajom a vyplniť výšku, rovnako ako builder pod ním.
    return (
      <VotingLockedView voting={votingDetail} onChanged={refreshVotings} />
    );
  }

  return (
    <Form<FormValues>
      initialValues={initialValues}
      mutators={{ ...arrayMutators }}
      keepDirtyOnReinitialize
      onSubmit={async (values) => {
        if (intentRef.current === 'publish') {
          setPublishValues(values);
          return;
        }
        await save(values, false);
      }}
    >
      {({ handleSubmit, values, form }) => {
        const questions = values.questions ?? [];
        const activeIndex =
          typeof selection === 'number'
            ? Math.min(selection, questions.length - 1)
            : -1;

        const addQuestion = (type: QuestionType) => {
          form.mutators.push('questions', emptyQuestion(type));
          setSelection(questions.length);
          setMobilePane('editor');
        };

        const move = (from: number, to: number) => {
          if (to < 0 || to >= questions.length) return;
          form.mutators.move('questions', from, to);
          setSelection(to);
        };

        const sidebar = (variant: 'desktop' | 'mobile') => (
          <VotingBuilderSidebar
            variant={variant}
            title={values.title}
            openFrom={values.openFrom}
            questions={questions}
            selection={selection}
            onSelect={(next) => {
              setSelection(next);
              setMobilePane('editor');
            }}
            onAdd={() => addQuestion('single_choice')}
            onMove={move}
          />
        );

        /**
         * `FieldArray` obaľuje **obe** vetvy, teda aj nastavenia, ktoré s poľom
         * otázok nesúvisia. Je to zámer: takto zostáva `questions`
         * zaregistrované aj vtedy, keď je otvorená druhá polovica buildera,
         * takže rozpísané otázky nezávisia od toho, či `final-form` práve má
         * `destroyOnUnregister` vypnuté. Prekreslenie nastavení navyše pri
         * zmene poľa nič nestojí.
         */
        const pane = (
          <FieldArray<QuestionValues> name='questions'>
            {({ fields }) =>
              selection === 'settings' ? (
                <VotingSettingsForm />
              ) : questions.length === 0 ? (
                <VotingEmptyState onPick={addQuestion} />
              ) : activeIndex >= 0 && fields.value?.[activeIndex] ? (
                <QuestionEditor
                  name={`questions.${activeIndex}`}
                  index={activeIndex}
                  total={questions.length}
                  value={fields.value[activeIndex]}
                  onUpdate={(next) => fields.update(activeIndex, next)}
                  onDuplicate={() => {
                    fields.insert(activeIndex + 1, {
                      ...fields.value[activeIndex],
                      title: `${fields.value[activeIndex].title} ${__('(kópia)')}`,
                    });
                    setSelection(activeIndex + 1);
                  }}
                  onRemove={() => {
                    fields.remove(activeIndex);
                    setSelection('settings');
                    setMobilePane('list');
                  }}
                />
              ) : null
            }
          </FieldArray>
        );

        return (
          <form onSubmit={handleSubmit} id='voting-builder-form'>
            <FormSpy
              subscription={{ dirty: true }}
              onChange={({ dirty }) => setIsFormDirty(dirty)}
            />

            {isMd ? (
              <Stack
                direction='row'
                sx={{
                  height: '100%',
                  minHeight: 0,
                  backgroundColor: 'background.paper',
                }}
              >
                {sidebar('desktop')}
                <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
                  <Box
                    sx={{
                      maxWidth: 720,
                      mx: 'auto',
                      px: 4,
                      py: 3.5,
                      minHeight: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {pane}
                  </Box>
                </Box>
              </Stack>
            ) : mobilePane === 'list' ? (
              sidebar('mobile')
            ) : (
              <Stack sx={{ p: 1.75, gap: 2 }}>
                <Button
                  variant='text'
                  size='small'
                  startIcon={<ArrowBackIcon fontSize='small' />}
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={() => setMobilePane('list')}
                >
                  {__('Späť na zoznam otázok')}
                </Button>
                {pane}
              </Stack>
            )}

            <PublishDialog
              open={!!publishValues}
              values={publishValues ?? values}
              loading={isSaving}
              onCancel={() => setPublishValues(null)}
              onFixDates={() => {
                setPublishValues(null);
                setSelection('settings');
                setMobilePane('editor');
              }}
              onConfirm={async () => {
                const toSave = publishValues;
                setPublishValues(null);
                if (toSave) await save(toSave, true);
              }}
            />
          </form>
        );
      }}
    </Form>
  );
};

export default VotingDetailAdminPage;
